import { useEffect, useRef } from 'react';
import { HEARTBEAT_FRAME, parseNotification, type SaleNotification } from './envelope';
import { HEARTBEAT_INTERVAL_MS, reconnectDelayMs } from './socketPolicy';
import {
    RECONNECT_ALERT_THRESHOLD,
    reportChannelDown,
    reportUnreadableEnvelope,
} from './telemetry';

interface Options {
    /** Solo se conecta con sesión iniciada: el handshake se autentica con la cookie `session`. */
    enabled: boolean;
    onNotification: (notification: SaleNotification) => void;
}

/** Clave del sobre sin `type` legible, para no reportar el mismo drift una vez por mensaje. */
const UNTYPED = '(sin tipo)';

/**
 * Un socket por vendedor logueado, vivo toda la sesión. La cookie viaja sola en el handshake (mismo
 * dominio registrable), así que no hay token que mandar: es `HttpOnly` y el front no la puede leer.
 *
 * El canal rutea por la identidad que el authorizer resuelve en el `$connect`, así que no hay nada
 * que suscribir después: toda reconexión vuelve a quedar suscrita por sí sola.
 */
export function useSaleNotificationsSocket({ enabled, onNotification }: Options): void {
    const url = import.meta.env.VITE_API_RETAIL_NOTIFICATIONS_WSS_URL;
    const handlerRef = useRef(onNotification);

    useEffect(() => {
        handlerRef.current = onNotification;
    }, [onNotification]);

    useEffect(() => {
        if (!enabled || !url) return;

        let disposed = false;
        let socket: WebSocket | null = null;
        let attempt = 0;
        let heartbeat: ReturnType<typeof setInterval> | undefined;
        let retry: ReturnType<typeof setTimeout> | undefined;
        // Un drift del contrato se reporta UNA vez por tipo, no una por mensaje.
        const reportedTypes = new Set<string>();

        const stopHeartbeat = (): void => {
            if (heartbeat !== undefined) clearInterval(heartbeat);
            heartbeat = undefined;
        };

        /** Cuenta el reintento y avisa al cruzar el umbral, una sola vez por caída. */
        const scheduleRetry = (): void => {
            const delay = reconnectDelayMs(attempt);
            attempt += 1;
            if (attempt === RECONNECT_ALERT_THRESHOLD) reportChannelDown(attempt);
            retry = setTimeout(open, delay);
        };

        const open = (): void => {
            if (disposed) return;

            let current: WebSocket;
            try {
                current = new WebSocket(url);
            } catch {
                // Una URL inválida falla en el constructor y nunca emite `close`: se reintenta aquí.
                scheduleRetry();
                return;
            }
            socket = current;

            current.onopen = () => {
                // Reinicia el backoff: sin esto un canal ya sano seguiría esperando hasta 30 s.
                attempt = 0;
                stopHeartbeat();
                heartbeat = setInterval(() => {
                    if (current.readyState === WebSocket.OPEN) current.send(HEARTBEAT_FRAME);
                }, HEARTBEAT_INTERVAL_MS);
            };

            current.onmessage = (event: MessageEvent) => {
                const parsed = parseNotification(event.data);
                if (parsed.ok) {
                    handlerRef.current(parsed.notification);
                    return;
                }
                const key = parsed.type ?? UNTYPED;
                if (reportedTypes.has(key)) return;
                reportedTypes.add(key);
                reportUnreadableEnvelope(parsed.type);
            };

            // Toda reconexión pasa por aquí, incluidas la caducidad de las 2 h y el vencimiento de
            // la sesión. `onerror` no reconecta: el navegador siempre emite `close` después.
            current.onclose = () => {
                stopHeartbeat();
                if (disposed) return;
                scheduleRetry();
            };
        };

        open();

        return () => {
            disposed = true;
            stopHeartbeat();
            if (retry !== undefined) clearTimeout(retry);
            socket?.close();
        };
    }, [enabled, url]);
}
